import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import Intercom from "@intercom/messenger-js-sdk";
import { ChevronDown, Menu, X } from "lucide-react";

import useCalApi from "@/components/hooks/useCalApi";
import { useCalendarLink } from "@/components/hooks/useCalendarLink";
import useDisableScrolling from "@/components/hooks/useDisableScrolling";
import { useUser } from "@/components/hooks/useUser";
import { Button } from "@/components/ui/button";
import { INTERCOM_APP_ID, PRODUCT_CATEGORIES, PRODUCTS } from "@/lib/constants";
import { COMPANY } from "@/lib/constants/marketing-items";
import { cn } from "@/lib/utils";
import { getProductHref } from "@/lib/utils/marketing";
import { clashGrotesk, jakartaSans } from "@/styles/fonts";

// --- types ---

interface NavbarProps {
  isFixed?: boolean;
  solidBackground?: boolean;
  showBanner?: boolean;
}

interface NavMenuItemProps {
  href: string;
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

// --- hooks ---

function useScrolled(threshold = 20) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const sentinel = document.createElement("div");
    Object.assign(sentinel.style, {
      position: "absolute",
      top: `${threshold}px`,
      height: "1px",
      width: "1px",
      opacity: "0",
    });
    document.body.appendChild(sentinel);

    const observer = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting), { threshold: 0 });
    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      sentinel.remove();
    };
  }, [threshold]);

  return scrolled;
}

function useThirdPartyScripts() {
  useEffect(() => {
    const schedule = (fn: () => void) =>
      "requestIdleCallback" in window ? window.requestIdleCallback(fn) : setTimeout(fn, 2000);

    schedule(() => {
      try {
        Intercom({ app_id: INTERCOM_APP_ID });
        if (typeof window !== "undefined" && window.Intercom) {
          window.Intercom("update", { hide_default_launcher: false });
        }
      } catch (error) {
        console.error("Error configuring Intercom", error);
      }
    });

    schedule(() => {
      const script = document.createElement("script");
      script.type = "text/javascript";
      script.src = "https://tracker.metricool.com/resources/be.js";
      script.onload = () => {
        const win = window as unknown as Record<string, unknown>;
        if (win.beTracker) {
          (win.beTracker as { t: (config: { hash: string }) => void }).t({
            hash: "e8dddb5a4ce88ccb1c2f0df1272651d",
          });
        }
      };
      document.head.appendChild(script);
    });
  }, []);
}

// --- primitives ---

function NavMenuItem({ href, label, isActive = false, onClick }: NavMenuItemProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="group/item flex items-center h-10 px-3 py-2 rounded-full transition-colors"
    >
      <span
        className={cn(
          "text-[14px] font-semibold leading-[1.35] group-hover/item:text-rebolt-500 transition-colors",
          isActive ? "text-rebolt-500" : "text-ash-300"
        )}
      >
        {label}
      </span>
    </Link>
  );
}

function NavDivider() {
  return (
    <div className="px-2 py-2">
      <div className="h-px bg-ash-700 shadow-sm" />
    </div>
  );
}

function NavLink({ href, label, isActive = false }: NavMenuItemProps) {
  return (
    <Link
      href={href}
      className={cn(
        "font-medium leading-normal transition-colors duration-200",
        isActive ? "text-rebolt-500" : "text-white"
      )}
    >
      {label}
    </Link>
  );
}

function BookDemoButton({ size = "lg", calPath }: { size?: "lg" | "sm"; calPath: string }) {
  return (
    <Button variant="rebolt-primary" size={size} data-cal-link={calPath} data-cal-config='{"layout":"month_view"}'>
      Book your demo
    </Button>
  );
}

// --- banner ---

function NavBanner({ visible }: { visible: boolean }) {
  return (
    <div
      className={cn(
        "overflow-hidden transition-all duration-500 ease-in-out pointer-events-auto",
        visible ? "max-h-12 opacity-100" : "max-h-0 opacity-0"
      )}
    >
      <div className="bg-linear-to-r from-rebolt-600 via-rebolt-500 to-rebolt-600 flex items-center justify-center h-12">
        <p className="relative overflow-hidden text-[10px] md:text-xs font-bold uppercase tracking-[1.1px] text-white text-center">
          <span
            className="absolute inset-0 -translate-x-full animate-[banner-shimmer_6s_cubic-bezier(0.4,0,0.2,1)_infinite]"
            style={{
              background:
                "linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.2) 46%, rgba(255,255,255,0.35) 50%, rgba(255,255,255,0.2) 54%, transparent 65%)",
            }}
          />
          <span className="relative hidden md:inline">
            ✦ New: The Rebolt CRM is live — lock in launch pricing before it&apos;s gone ✦
          </span>
          <span className="relative md:hidden">✦ CRM is live — lock in launch pricing ✦</span>
        </p>
      </div>
    </div>
  );
}

// --- desktop dropdowns ---

function NavDropdown({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="relative group">
      <button className="flex items-center gap-2 text-[16px] font-medium text-white group-hover:text-rebolt-500 leading-normal transition-colors duration-200 cursor-pointer">
        {label}
        <ChevronDown className="w-5 h-5 transition-transform duration-200 group-hover:rotate-180" />
      </button>
      <div className="absolute left-1/2 -translate-x-1/2 py-3 px-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 min-w-[200px]">
        <div className="bg-ash-800 rounded-3xl overflow-hidden px-1 py-3">
          <div className="flex flex-col p-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

// --- mobile menu ---

function MobileMenuPanel({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <>
      {/* full-screen backdrop — behind nav siblings via -z-10, still covers page content since parent is z-40 */}
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 -z-10 bg-black/50 backdrop-blur-[2px] xl:hidden transition-opacity duration-300",
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        )}
      />
      {/* menu panel */}
      <div
        className={cn(
          "xl:hidden transition-all duration-300 ease-in-out px-4 mt-4 relative",
          isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-4 pointer-events-none"
        )}
      >
        <div className="bg-ash-900 border border-ash-800 rounded-3xl overflow-hidden px-1 py-3 max-h-[70vh] overflow-y-auto scrollbar-hide">
          <div className="p-1">{children}</div>
        </div>
      </div>
    </>
  );
}

function MobileSectionHeader({ label }: { label: string }) {
  return (
    <div className="px-3 py-2">
      <span className="text-sm font-semibold text-ash-400">{label}</span>
    </div>
  );
}

// --- main ---

export default function Navbar({ isFixed = true, solidBackground = false, showBanner = true }: NavbarProps) {
  const { user } = useUser();
  const router = useRouter();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const hasScrolled = useScrolled();
  useCalApi();
  const { calPath } = useCalendarLink();
  useThirdPartyScripts();
  useDisableScrolling(mobileMenuOpen);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [router.asPath]);

  // close mobile menu when resizing past lg breakpoint
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const handler = () => {
      if (mql.matches) setMobileMenuOpen(false);
    };
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  const crmProducts = PRODUCTS.filter((p) => p.category === PRODUCT_CATEGORIES.CRM && !p.isComingSoon);
  const isActive = (href: string) => router.asPath === href || router.asPath.startsWith(href + "/");
  const isLoggedIn = !!user?.id;

  return (
    <div
      className={cn(
        "z-40 transition-all duration-500 ease-in-out pointer-events-none",
        isFixed ? "fixed inset-x-0 top-0" : "absolute inset-x-0 top-0",
        jakartaSans.variable,
        clashGrotesk.variable
      )}
    >
      <NavBanner visible={showBanner} />

      <nav
        className={cn(
          "w-full transition-all duration-500 pointer-events-auto",
          hasScrolled ? "bg-black/50 backdrop-blur-sm" : solidBackground ? "bg-black/90" : "bg-transparent"
        )}
      >
        <div className="relative flex items-center justify-between px-5 xl:px-28 py-4">
          <Link href="/" className="shrink-0 relative z-10">
            <img src="/rebolt-branding/rebolt-half-white.svg" className="h-[30px]" alt="Rebolt" />
          </Link>

          {/* desktop center */}
          <div className="hidden xl:flex items-center gap-14 absolute inset-x-0 justify-center">
            <NavDropdown label="Solutions">
              {crmProducts.map((p) => (
                <NavMenuItem key={p.productKey} href={getProductHref(p)} label={p.shortName} />
              ))}
              <NavMenuItem href="/ai-voice-assistant" label="AI Voice Assistant" />
              <NavMenuItem href="/#marketing-kit" label="Marketing Kit" />
            </NavDropdown>
            <NavLink href="/pricing" label="Pricing" isActive={isActive("/pricing")} />
            <NavLink href="/case-studies" label="Case Studies" isActive={isActive("/case-studies")} />
            <NavDropdown label="Company">
              {COMPANY.map((item) => (
                <NavMenuItem key={item.name} href={item.slug} label={item.name} />
              ))}
            </NavDropdown>
          </div>

          {/* desktop right */}

          <div className="hidden xl:flex items-center gap-6 relative z-10">
            {isLoggedIn ? (
              <Button variant="rebolt-primary" size="lg" asChild>
                <Link href="/dashboard">Dashboard</Link>
              </Button>
            ) : (
              <>
                <Link href="/sign-in" className="text-[16px] font-medium text-white">
                  Login
                </Link>
                <BookDemoButton calPath={calPath} />
              </>
            )}
          </div>

          {/* mobile right */}
          <div className="flex xl:hidden items-center gap-3 relative z-10">
            {!mobileMenuOpen && (
              <>
                {isLoggedIn ? (
                  <Button variant="rebolt-primary" size="sm" asChild>
                    <Link href="/dashboard">Dashboard</Link>
                  </Button>
                ) : (
                  <BookDemoButton size="sm" calPath={calPath} />
                )}
              </>
            )}

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
              className={cn(
                "size-9 flex items-center justify-center rounded-full cursor-pointer",
                mobileMenuOpen ? "bg-white border border-ash-300" : "bg-transparent"
              )}
            >
              {mobileMenuOpen ? <X className="size-4 text-ash-900" /> : <Menu className="size-6 text-white" />}
            </button>
          </div>
        </div>
      </nav>

      <MobileMenuPanel isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <MobileSectionHeader label="Solutions" />
        {crmProducts.map((p) => (
          <NavMenuItem key={p.productKey} href={getProductHref(p)} label={p.shortName} />
        ))}
        <NavMenuItem href="/#marketing-kit" label="Marketing Kit" onClick={() => setMobileMenuOpen(false)} />
        <NavMenuItem href="/ai-voice-assistant" label="AI Voice Assistant" />
        <NavDivider />
        <NavMenuItem href="/pricing" label="Pricing" isActive={isActive("/pricing")} />
        <NavDivider />
        <NavMenuItem href="/case-studies" label="Case Studies" isActive={isActive("/case-studies")} />
        <NavDivider />
        <MobileSectionHeader label="Company" />
        {COMPANY.map((item) => (
          <NavMenuItem key={item.name} href={item.slug} label={item.name} onClick={() => setMobileMenuOpen(false)} />
        ))}
        <NavDivider />
        <NavMenuItem href={isLoggedIn ? "/dashboard" : "/sign-in"} label={isLoggedIn ? "Dashboard" : "Login"} />
      </MobileMenuPanel>
    </div>
  );
}
